import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../services/api';
import '../../styles/monitoring-dashboard.css';

const MonitoringDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Dashboard data
  const [summary, setSummary] = useState(null);
  const [systemHealth, setSystemHealth] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [platformStats, setPlatformStats] = useState([]);
  const [apiPerformance, setApiPerformance] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);
  const [databaseDiagnostics, setDatabaseDiagnostics] = useState(null);

  // Fetch all monitoring data
  const fetchMonitoringData = async () => {
    try {
      setError(null);
      
      // Fetch dashboard summary
      const summaryRes = await api.get('/admin/monitoring/dashboard');
      setSummary(summaryRes.data.data);

      // Fetch system health (last 24 hours)
      const healthRes = await api.get('/admin/monitoring/system-health?hours=24&limit=500');
      setSystemHealth(healthRes.data.data);

      // Fetch active users (last 7 days) - aumentato limite per coprire 7 giorni di dati
      const usersRes = await api.get('/admin/monitoring/active-users?hours=168&limit=2500');
      setActiveUsers(usersRes.data.data.history);

      // Fetch platform stats (last 30 days)
      const statsRes = await api.get('/admin/monitoring/platform-stats?days=30&limit=100');
      setPlatformStats(statsRes.data.data);

      // Fetch API performance
      const perfRes = await api.get('/admin/monitoring/api-performance?hours=24');
      setApiPerformance(perfRes.data.data);

      // Fetch recent errors
      const errorsRes = await api.get('/admin/monitoring/errors?limit=20');
      setRecentErrors(errorsRes.data.data);

      // Fetch database diagnostics
      try {
        const dbDiagRes = await api.get('/health/database-diagnostics');
        setDatabaseDiagnostics(dbDiagRes.data.data);
      } catch (dbErr) {
        console.warn('Database diagnostics not available:', dbErr);
        // Non-critical, continua anche se fallisce
      }

      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching monitoring data:', err);
      setError(err.response?.data?.message || 'Errore nel caricamento dei dati di monitoring');
      setLoading(false);
    }
  };

  // Manual refresh
  const handleManualRefresh = async () => {
    setLoading(true);
    await fetchMonitoringData();
  };

  // Trigger manual monitoring check
  const handleRunCheck = async () => {
    try {
      await api.post('/admin/monitoring/run-check');
      alert('Check di monitoring eseguito! I dati si aggiorneranno tra poco.');
      setTimeout(() => fetchMonitoringData(), 2000);
    } catch (err) {
      alert('Errore nell\'esecuzione del check: ' + err.message);
    }
  };

  // Initial load and auto-refresh
  useEffect(() => {
    fetchMonitoringData();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchMonitoringData();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Format timestamp for charts
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('it-IT', { month: 'short', day: 'numeric' });
  };

  // Format per grafici multi-giorno (mostra data + ora se il range è > 24h)
  const formatDateTimeForChart = (timestamp, dataArray) => {
    if (!dataArray || dataArray.length === 0) return formatTimestamp(timestamp);
    
    const firstDate = new Date(dataArray[0].timestamp);
    const lastDate = new Date(dataArray[dataArray.length - 1].timestamp);
    const hoursDiff = (lastDate - firstDate) / (1000 * 60 * 60);
    
    // Se il range è più di 24 ore, mostra data + ora
    if (hoursDiff > 24) {
      const date = new Date(timestamp);
      return date.toLocaleDateString('it-IT', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit'
      });
    }
    
    return formatTimestamp(timestamp);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const variant = status === 'online' ? 'success' : status === 'offline' ? 'danger' : 'warning';
    return <Badge bg={variant}>{status.toUpperCase()}</Badge>;
  };

  // Severity badge component
  const SeverityBadge = ({ severity }) => {
    const colors = {
      critical: 'danger',
      error: 'warning',
      warning: 'info',
      info: 'secondary'
    };
    return <Badge bg={colors[severity] || 'secondary'}>{severity}</Badge>;
  };

  if (loading && !summary) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
        <p className="mt-3">Caricamento dashboard monitoring...</p>
      </Container>
    );
  }

  if (error && !summary) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Errore</Alert.Heading>
          <p>{error}</p>
          <Button onClick={handleManualRefresh}>Riprova</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="monitoring-dashboard mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2>🔍 Dashboard Monitoring</h2>
          <p className="text-muted">
            Ultimo aggiornamento: {lastUpdate.toLocaleString('it-IT')}
            {autoRefresh && <span className="ms-2">(Auto-refresh: 30s)</span>}
          </p>
        </Col>
        <Col xs="auto">
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={handleManualRefresh}
            disabled={loading}
            className="me-2"
          >
            {loading ? <Spinner size="sm" animation="border" /> : '🔄 Aggiorna'}
          </Button>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="me-2"
          >
            {autoRefresh ? '⏸️ Pausa' : '▶️ Auto'}
          </Button>
          <Button 
            variant="outline-success" 
            size="sm" 
            onClick={handleRunCheck}
          >
            ⚡ Esegui Check
          </Button>
        </Col>
      </Row>

      {/* Status Cards */}
      {summary && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title className="text-muted small">MariaDB</Card.Title>
                <div className="d-flex align-items-center justify-content-between">
                  <StatusBadge status={summary.systemHealth.mariadbStatus} />
                  {summary.systemHealth.mariaResponseTime && (
                    <span className="text-muted small">{summary.systemHealth.mariaResponseTime}ms</span>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title className="text-muted small">Utenti Totali</Card.Title>
                <h3 className="mb-0">{summary.platformStats?.totalUsers || 0}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title className="text-muted small">Server</Card.Title>
                <div className="small">
                  <div>CPU: {summary.systemHealth.cpuUsage?.toFixed(1) || 0}%</div>
                  <div>RAM: {summary.systemHealth.memoryUsage ? Math.round(summary.systemHealth.memoryUsage) : 0}MB</div>
                  <div>Uptime: {summary.systemHealth.serverUptime ? Math.floor(summary.systemHealth.serverUptime / 3600) : 0}h</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* System Health Chart */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <strong>📊 Salute Sistema (Ultime 24 ore)</strong>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={systemHealth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={formatTimestamp}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleString('it-IT')}
                  />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="mariadb_response_time_ms" 
                    stroke="#82ca9d" 
                    name="MariaDB (ms)"
                    dot={false}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="cpu_usage_percent" 
                    stroke="#ffc658" 
                    name="CPU %"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Active Users & Platform Stats */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>
              <strong>👥 Utenti Attivi (7 Giorni)</strong>
              {activeUsers && activeUsers.length > 0 && (
                <span className="text-muted ms-2">
                  ({activeUsers.length} record{activeUsers.length > 1 ? 's' : ''})
                </span>
              )}
            </Card.Header>
            <Card.Body>
              {activeUsers && activeUsers.length > 0 ? (
                <>
                  {activeUsers.length < 100 && (
                    <Alert variant="info" className="mb-3 py-2 small">
                      ℹ️ Dati limitati disponibili. Il monitoring è attivo da poco tempo o è stato riavviato di recente.
                    </Alert>
                  )}
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={activeUsers}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="timestamp" 
                        tickFormatter={(value) => formatDateTimeForChart(value, activeUsers)}
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(value) => new Date(value).toLocaleString('it-IT')}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="admin_users" 
                        stackId="1"
                        stroke="#ff7300" 
                        fill="#ff7300" 
                        name="Admin"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="uisp_users" 
                        stackId="1"
                        stroke="#8884d8" 
                        fill="#8884d8" 
                        name="UISP"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="association_users" 
                        stackId="1"
                        stroke="#82ca9d" 
                        fill="#82ca9d" 
                        name="Associazioni"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="sda_users" 
                        stackId="1"
                        stroke="#a0a0a0" 
                        fill="#a0a0a0" 
                        name="SDA"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <Alert variant="warning">
                  ⚠️ Nessun dato disponibile. Il monitoring non ha ancora raccolto dati sugli utenti attivi.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>
              <strong>📈 Crescita Piattaforma (30 Giorni)</strong>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={platformStats.slice(-30)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString('it-IT')}
                  />
                  <Legend />
                  <Bar dataKey="total_events" fill="#8884d8" name="Eventi" />
                  <Bar dataKey="total_associations" fill="#82ca9d" name="Associazioni" />
                  <Bar dataKey="total_members" fill="#ffc658" name="Soci" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* API Performance */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="h-100">
            <Card.Header>
              <strong>⚡ API Performance (Endpoint più lenti)</strong>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {apiPerformance && apiPerformance.slowEndpoints.length > 0 ? (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Avg (ms)</th>
                      <th>Max (ms)</th>
                      <th>Richieste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiPerformance.slowEndpoints.map((item, idx) => (
                      <tr key={idx}>
                        <td className="small">{item.endpoint}</td>
                        <td><Badge bg="info">{item.method}</Badge></td>
                        <td>{Math.round(item.avg_response_time)}</td>
                        <td>{Math.round(item.max_response_time)}</td>
                        <td>{item.request_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">Nessun dato disponibile</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Errors */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <strong>🔴 Errori Recenti</strong>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {recentErrors.length > 0 ? (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th>Messaggio</th>
                      <th>Endpoint</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentErrors.map((error, idx) => (
                      <tr key={idx}>
                        <td><SeverityBadge severity={error.severity} /></td>
                        <td className="small">{error.error_message}</td>
                        <td className="small">{error.endpoint || 'N/A'}</td>
                        <td className="small">
                          {new Date(error.timestamp).toLocaleString('it-IT')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="success">
                  ✅ Nessun errore recente
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Platform Statistics Summary */}
      {summary && summary.platformStats && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card>
              <Card.Header>
                <strong>📊 Statistiche Piattaforma</strong>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <h5>{summary.platformStats.totalEvents}</h5>
                    <p className="text-muted small">Eventi Totali</p>
                  </Col>
                  <Col md={3}>
                    <h5>{summary.platformStats.publishedEvents}</h5>
                    <p className="text-muted small">Eventi Pubblicati</p>
                  </Col>
                  <Col md={3}>
                    <h5>{summary.platformStats.totalAssociations}</h5>
                    <p className="text-muted small">Associazioni</p>
                  </Col>
                  <Col md={3}>
                    <h5>{summary.platformStats.totalMembers}</h5>
                    <p className="text-muted small">Soci</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Database Diagnostics */}
      {databaseDiagnostics && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card>
              <Card.Header>
                <strong>🗄️ Diagnostica Database</strong>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  {/* MariaDB Status */}
                  <Col md={12}>
                    <div className="border rounded p-3">
                      <h6 className="mb-2">MariaDB</h6>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Status:</span>
                        <Badge 
                          bg={
                            databaseDiagnostics.currentStatus?.status?.mariadb?.severity === 0 ? 'success' :
                            databaseDiagnostics.currentStatus?.status?.mariadb?.severity === 1 ? 'info' :
                            databaseDiagnostics.currentStatus?.status?.mariadb?.severity === 2 ? 'warning' :
                            databaseDiagnostics.currentStatus?.status?.mariadb ? 'danger' : 'secondary'
                          }
                        >
                          {databaseDiagnostics.currentStatus?.status?.mariadb?.status?.toUpperCase() ?? 'N/A'}
                        </Badge>
                      </div>
                      <small className="text-muted d-block mb-2">{databaseDiagnostics.currentStatus?.status?.mariadb?.message ?? 'Dati non disponibili'}</small>
                      
                      {/* Pool Metrics */}
                      <table className="w-100 small">
                        <tbody>
                          <tr>
                            <td className="pe-2">Connessioni Attive:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.poolDetails.activeConnections}/{databaseDiagnostics.poolDetails.totalConnections}</td>
                          </tr>
                          <tr>
                            <td className="pe-2">Utilizzo:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.poolDetails.utilizationPercent}%</td>
                          </tr>
                          <tr>
                            <td className="pe-2">In Coda:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.poolDetails.queuedRequests}</td>
                          </tr>
                          <tr>
                            <td className="pe-2">Limite Connessioni:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.poolDetails.connectionLimit}</td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Visualization of utilization */}
                      <div className="mt-2">
                        <div className="progress" style={{ height: '8px' }}>
                          <div 
                            className={`progress-bar ${
                              databaseDiagnostics.poolDetails.utilizationPercent > 80 ? 'bg-danger' :
                              databaseDiagnostics.poolDetails.utilizationPercent > 60 ? 'bg-warning' :
                              'bg-success'
                            }`}
                            role="progressbar"
                            style={{ width: `${Math.min(databaseDiagnostics.poolDetails.utilizationPercent, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Health Check Stats */}
                {databaseDiagnostics.aggregatedStats && (
                  <Row className="mt-3 pt-3 border-top">
                    <Col md={12}>
                      <h6 className="small mb-2">📊 Statistiche Ultime 1 Ora - MariaDB</h6>
                      <table className="w-100 small">
                        <tbody>
                          <tr>
                            <td className="pe-2">Check Totali:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.aggregatedStats.totalChecks}</td>
                          </tr>
                          <tr>
                            <td className="pe-2">Severità Media:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.aggregatedStats.mariadb?.avgSeverity ?? 'N/A'}</td>
                          </tr>
                          <tr>
                            <td className="pe-2">Severità Max:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.aggregatedStats.mariadb?.maxSeverity ?? 'N/A'}</td>
                          </tr>
                          <tr>
                            <td className="pe-2">Uptime:</td>
                            <td className="text-end fw-bold">{databaseDiagnostics.aggregatedStats.mariadb?.healthyPercentage ?? 'N/A'}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </Col>
                  </Row>
                )}

                {/* Recommendations */}
                {databaseDiagnostics.recommendations && databaseDiagnostics.recommendations.length > 0 && (
                  <Row className="mt-3 pt-3 border-top">
                    <Col lg={12}>
                      <h6 className="small mb-2">⚠️ Raccomandazioni</h6>
                      {databaseDiagnostics.recommendations.map((rec, idx) => (
                        <Alert 
                          key={idx} 
                          variant={rec.severity === 'HIGH' ? 'danger' : rec.severity === 'MEDIUM' ? 'warning' : 'info'}
                          className="mb-2 py-2"
                        >
                          <strong className="small">{rec.issue}</strong>
                          <br />
                          <small>{rec.action}</small>
                        </Alert>
                      ))}
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default MonitoringDashboard;
